import React from 'react';

import { SURFACE, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { LensClarification, LensClarificationOption, LensScope } from '~/types/leLens';

import { SectionHeading, SurfacePlate } from './surface';

/**
 * The five plain-English terms the confirmation block always shows, in a fixed order, regardless
 * of which of them the planner or the deterministic detector (svc-reporting scope.ts) actually
 * filled in. `metric_basis` and `geography` are the wire ids the detector uses (matching
 * `LensClarification.id`); the on-screen label is deliberately plainer than either, e.g.
 * "Region means" rather than "Geography".
 */
export type ScopeRowId = 'dataset' | 'metric_basis' | 'period' | 'geography' | 'attribution';

export const SCOPE_ROWS: ReadonlyArray<{ id: ScopeRowId; label: string }> = [
  { id: 'dataset', label: 'Dataset' },
  { id: 'metric_basis', label: 'Metric basis' },
  { id: 'period', label: 'Period' },
  { id: 'geography', label: 'Region means' },
  { id: 'attribution', label: 'Attribution' },
];

/** True when `comparison.label` already opens with its own "vs" (svc-reporting's usual
 *  convention, e.g. "vs the same days last month"). Some labels do not (e.g. "Previous 30
 *  completed Sydney days" alongside a period label that is only a window's own name, not a
 *  continuation sentence), and those need the joiner supplied here instead of doubling up. */
function comparisonLabelStartsWithVs(label: string): boolean {
  return /^vs\b/i.test(label.trim());
}

/** The row's resolved value in plain English, or null when the planner has nothing to state for
 *  it. That is not the same as unresolved: a brief with no geography words at all still has a
 *  null geography and no clarification for it.
 *
 *  `datasetNameFallback` fills the dataset row when `scope.dataset` is itself null but the plan
 *  or the saved spec binds exactly one dataset regardless (a routed plan's own `scope.dataset` is
 *  null until every card is bound to a single answer, and a saved Lens with one dataset is not
 *  the same fact as the planner having named one, but it is a fact this row should still state
 *  rather than reading "Not set" over a board that plainly reads from one place). */
export function scopeRowValue(
  id: ScopeRowId,
  scope: LensScope | undefined,
  datasetNameFallback?: string,
): string | null {
  if (!scope) {
    return null;
  }
  switch (id) {
    case 'dataset':
      return scope.dataset?.name ?? datasetNameFallback ?? null;
    case 'metric_basis':
      return scope.metricBasis?.label ?? null;
    case 'period':
      if (!scope.period) {
        return null;
      }
      if (!scope.comparison) {
        return scope.period.label;
      }
      return comparisonLabelStartsWithVs(scope.comparison.label)
        ? `${scope.period.label} ${scope.comparison.label}`
        : `${scope.period.label} vs ${scope.comparison.label}`;
    case 'geography':
      return scope.geography?.label ?? null;
    case 'attribution':
      if (!scope.attribution) {
        return null;
      }
      return 'notApplicable' in scope.attribution ? 'Not applicable' : scope.attribution.label;
    default:
      return null;
  }
}

const KNOWN_ROW_IDS: ReadonlySet<string> = new Set(SCOPE_ROWS.map((row) => row.id));

/** Capitalises a clarification's own free-text term for use as a row label, for the (rare) id the
 *  detector mints that is not one of the five known rows, e.g. 'brand'. */
function fallbackRowLabel(clarification: LensClarification): string {
  const source = clarification.term.trim() || clarification.id;
  return source.charAt(0).toUpperCase() + source.slice(1);
}

/**
 * Sorts the planner's clarifications into the five fixed rows and whatever is left over. A
 * second clarification for a row already claimed (which the server should never send, since it
 * "must not return a clarification whose id is in resolutions", but this reads defensively rather
 * than trusting that) falls through to `extra` rather than silently replacing the first, so no
 * clarification a reader must answer before Build ever disappears from the screen.
 */
export function groupScopeClarifications(clarifications: ReadonlyArray<LensClarification>): {
  byRowId: Map<ScopeRowId, LensClarification>;
  extra: Array<LensClarification>;
} {
  const byRowId = new Map<ScopeRowId, LensClarification>();
  const extra: Array<LensClarification> = [];
  for (const clarification of clarifications) {
    if (KNOWN_ROW_IDS.has(clarification.id) && !byRowId.has(clarification.id as ScopeRowId)) {
      byRowId.set(clarification.id as ScopeRowId, clarification);
    } else {
      extra.push(clarification);
    }
  }
  return { byRowId, extra };
}

export function ResolvedRow({ label, value }: { label: string; value: string | null }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      spacing={2}
      sx={{
        borderTop: `1px solid ${alpha(tokens.accent, SURFACE.hairlineAlphaSubtle)}`,
        minHeight: SURFACE.rowHeight,
        px: 1.5,
      }}
    >
      <Typography color="text.secondary" sx={{ flexShrink: 0, fontSize: 13.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'right' }}>{value ?? 'Not set'}</Typography>
    </Stack>
  );
}

function ClarificationRow({
  clarification,
  label,
  onResolve,
  resolving,
}: {
  clarification: LensClarification;
  label: string;
  onResolve: (clarification: LensClarification, option: LensClarificationOption) => void;
  resolving: boolean;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box
      sx={{
        backgroundColor: alpha(tokens.accent, 0.035),
        borderTop: `1px solid ${alpha(tokens.accent, SURFACE.hairlineAlphaSubtle)}`,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack alignItems="baseline" direction="row" justifyContent="space-between" spacing={1}>
        <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
          {label}
        </Typography>
        <Typography sx={{ color: tokens.accent, fontSize: 12.5, fontWeight: 600 }}>
          {resolving ? 'Applying…' : 'Choose one to continue'}
        </Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.75 }}>
        &ldquo;{clarification.term}&rdquo;
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1, mt: 0.25 }}>{clarification.question}</Typography>
      <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1}>
        {clarification.options.map((option) => (
          <SurfacePlate
            aria-label={`${option.label}: ${option.description}`}
            interactive
            key={option.id}
            onClick={() => {
              if (!resolving) {
                onResolve(clarification, option);
              }
            }}
            sx={{ flex: 1, opacity: resolving ? 0.6 : 1, pointerEvents: resolving ? 'none' : 'auto' }}
          >
            <Stack spacing={0.25}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{option.label}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                {option.description}
              </Typography>
            </Stack>
          </SurfacePlate>
        ))}
      </Stack>
    </Box>
  );
}

function GeographyValueRow({
  error,
  label,
  loading,
  onChange,
  onRetry,
  options,
  rankedBy,
  value,
}: {
  error?: string;
  label: string;
  loading: boolean;
  onChange: (value: string) => void;
  onRetry?: () => void;
  options: ReadonlyArray<string>;
  rankedBy?: string;
  value: string;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const helperText = error
    ? error
    : loading
      ? 'Finding the regions used in recent reporting…'
      : rankedBy
        ? `Most relevant by ${rankedBy} are shown first.`
        : 'Choose the patch this Lens should open on.';

  return (
    <Box
      sx={{
        backgroundColor: alpha(tokens.accent, 0.035),
        borderTop: `1px solid ${alpha(tokens.accent, SURFACE.hairlineAlphaSubtle)}`,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack alignItems={{ sm: 'center', xs: 'stretch' }} direction={{ sm: 'row', xs: 'column' }} spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Which {label.toLowerCase()} is yours?</Typography>
          <Typography color={error ? 'error' : 'text.secondary'} sx={{ fontSize: 12.5, mt: 0.25 }}>
            {helperText}
          </Typography>
        </Box>
        {error && onRetry ? (
          <Button onClick={onRetry} size="small" variant="text">
            Try again
          </Button>
        ) : error ? null : (
          <Autocomplete
            autoHighlight
            disabled={loading}
            loading={loading}
            onChange={(_event, nextValue) => onChange(nextValue ?? '')}
            options={[...options]}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Your ${label.toLowerCase()}`}
                placeholder="Search regions"
                required
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: { sm: 320, xs: '100%' } }}
            value={value || null}
          />
        )}
      </Stack>
    </Box>
  );
}

export type ScopeConfirmationProps = {
  /** The planner's interpreted scope. Undefined while a plan has not returned one yet: an older
   *  service, or a "From your Lenses" start whose picked dataset is not governed. Every row then
   *  renders its resolved-but-empty state rather than a clarification, since there is nothing to
   *  ask about and nothing to block Build with. */
  scope: LensScope | undefined;
  /** Terms the planner could not resolve. Build stays locked while this is non-empty. */
  clarifications: ReadonlyArray<LensClarification>;
  /** The clarification id currently being applied, so its own options disable while the re-plan
   *  they triggered is in flight, without disabling every other row. */
  resolvingId?: string | null;
  onResolve: (clarification: LensClarification, option: LensClarificationOption) => void;
  /** The one dataset this plan or spec binds, named here only when it is the *only* one: a
   *  routed plan's `scope.dataset` stays null until every card lands on a single answer, but the
   *  reader should not read "Not set" over a board that plainly reads from one place. Left
   *  undefined when there is more than one bound dataset or none at all, in which case the row
   *  falls back to `scope.dataset` alone. */
  datasetNameFallback?: string;
  geographyValue?: string;
  geographyOptions?: ReadonlyArray<string>;
  geographyValuesLoading?: boolean;
  geographyValuesError?: string;
  geographyValuesRankedBy?: string;
  onGeographyValueChange?: (value: string) => void;
  onRetryGeographyValues?: () => void;
};

/**
 * The scope confirmation block (00_SYNTHESIS.md's step 3, journey_visual.png): one plate, five
 * plain-English rows in a fixed order, each either a resolved fact from `scope` or a question with
 * its candidate answers when the planner could not resolve it. Rendered once, above the card rows,
 * identically for a cold brief, a catalogue-anchored brief, "Build Lens from this answer", and (when
 * the picked dataset is governed) "From your Lenses". The acceptance test this exists for: a BDM
 * asking for "my region" cannot reach Build until they name the field.
 */
export default function ScopeConfirmation({
  clarifications,
  datasetNameFallback,
  geographyOptions = [],
  geographyValue = '',
  geographyValuesError,
  geographyValuesLoading = false,
  geographyValuesRankedBy,
  onGeographyValueChange,
  onRetryGeographyValues,
  onResolve,
  resolvingId,
  scope,
}: ScopeConfirmationProps) {
  const { byRowId: clarificationByRowId, extra: extraClarifications } = groupScopeClarifications(clarifications);

  return (
    <Box sx={{ mb: 2.5 }}>
      <SectionHeading
        description="askRichard read this brief the way an analyst would. Anything it could not resolve on its own needs your answer before Build."
        eyebrow="Scope"
        title="Confirm the scope"
      />
      <SurfacePlate sx={{ mt: 1.5, p: 0 }}>
        {SCOPE_ROWS.map(({ id, label }) => {
          const clarification = clarificationByRowId.get(id);
          if (clarification) {
            return (
              <ClarificationRow
                clarification={clarification}
                key={id}
                label={label}
                onResolve={onResolve}
                resolving={resolvingId === clarification.id}
              />
            );
          }
          return (
            <React.Fragment key={id}>
              <ResolvedRow label={label} value={scopeRowValue(id, scope, datasetNameFallback)} />
              {id === 'geography' && scope?.geography && onGeographyValueChange ? (
                <GeographyValueRow
                  error={geographyValuesError}
                  label={scope.geography.label}
                  loading={geographyValuesLoading}
                  onChange={onGeographyValueChange}
                  onRetry={onRetryGeographyValues}
                  options={geographyOptions}
                  rankedBy={geographyValuesRankedBy}
                  value={geographyValue}
                />
              ) : null}
            </React.Fragment>
          );
        })}
        {extraClarifications.map((clarification) => (
          <ClarificationRow
            clarification={clarification}
            key={clarification.id}
            label={fallbackRowLabel(clarification)}
            onResolve={onResolve}
            resolving={resolvingId === clarification.id}
          />
        ))}
      </SurfacePlate>
    </Box>
  );
}

/** One sentence for the reason text beside a locked Build button. Exported so PlanGateDialog can
 *  reuse the exact wording rather than re-deriving it. */
export function scopeBuildLockReason(clarifications: ReadonlyArray<LensClarification>): string | null {
  if (!clarifications.length) {
    return null;
  }
  if (clarifications.length === 1) {
    return `Choose what "${clarifications[0].term}" means to continue.`;
  }
  return `Answer ${clarifications.length} scope questions to continue.`;
}

/** One segment of the canvas scope line, in the same words the confirmation block itself uses.
 *  `geographyFilterValue` is the *current* value of the filter `scopeGeographyFilter` minted for
 *  this field (empty until the reader picks one): the scope itself only ever states what "region"
 *  *means*, never which one is theirs, so the segment has to carry the filter's own live value to
 *  say anything more specific than that. */
function formatScopeSegment(
  id: ScopeRowId,
  scope: LensScope,
  geographyFilterValue?: string | Array<string>,
  datasetNameFallback?: string,
): string | null {
  const value = scopeRowValue(id, scope, datasetNameFallback);
  if (!value) {
    return null;
  }
  if (id === 'geography') {
    const chosen = Array.isArray(geographyFilterValue) ? geographyFilterValue.join(', ') : geographyFilterValue;
    return chosen ? `Region = ${value}: ${chosen}` : `Region = ${value} · choose yours in the filter`;
  }
  if (id === 'attribution') {
    return value === 'Not applicable' ? 'Attribution not applicable' : `Attribution: ${value}`;
  }
  return value;
}

/**
 * The one line LeLensPage shows under the Lens title once it is saved: "Sales · TTV, before
 * refunds · 1 to 23 Sep vs 1 to 23 Aug · Region = destination region: Bali · Attribution not
 * applicable" (journey_visual.png; the geography segment reads "· choose yours in the filter"
 * until `geographyFilterValue` names one). Null when the Lens carries no scope at all, e.g. one
 * saved before this existed; a term the planner had nothing to say about is simply left out
 * rather than shown as a blank segment.
 */
export function formatScopeLine(
  scope: LensScope | undefined,
  geographyFilterValue?: string | Array<string>,
  datasetNameFallback?: string,
  options: {
    includeDataset?: boolean;
    includeMetricBasis?: boolean;
    includeNotApplicableAttribution?: boolean;
  } = {},
): string | null {
  if (!scope) {
    return null;
  }
  const segments = SCOPE_ROWS.filter(({ id }) => {
    if (id === 'dataset' && options.includeDataset === false) return false;
    if (id === 'metric_basis' && options.includeMetricBasis === false) return false;
    if (
      id === 'attribution' &&
      options.includeNotApplicableAttribution === false &&
      scope.attribution &&
      'notApplicable' in scope.attribution
    ) {
      return false;
    }
    return true;
  })
    .map(({ id }) => formatScopeSegment(id, scope, geographyFilterValue, datasetNameFallback))
    .filter((segment): segment is string => Boolean(segment));
  return segments.length ? segments.join(' · ') : null;
}
